import React, { createContext, useCallback, useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { trackSearchQuerySubmitted } from '~/search/actions';
import { productDetailPath } from '~/shared/constants/product';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Router } from '~/techstyle-shared/next-routes';
import { trackProductClicked } from '~/techstyle-shared/react-products';
import {
  getQueryParametersFromURL,
  useSessionActions,
} from '~/techstyle-shared/redux-core';

import { useCioSearchHistory } from './useCioSearchHistory';

const CioAutosuggestContext = createContext();

export const useCioAutosuggest = () => {
  const context = useContext(CioAutosuggestContext);
  return context || {};
};

export const CioAutosuggestProvider = ({ children }) => {
  // First check if user was redirected to a search page, if so initialize
  // query and inputValue to the search param.
  const url = new URL(window.location);
  const initSearchParam = url.searchParams.get('q');
  const [query, setQuery] = useState(initSearchParam || '');
  const [inputValue, setInputValue] = useState(initSearchParam || '');
  // no-op function. Can't use just () => {} as by default useState uses the
  // return value of a function to initial state.
  const [closeOverlay, setCloseOverlay] = useState(() => () => {});
  const dispatch = useDispatch();
  const { addHistory } = useCioSearchHistory();
  const secondarySite = useSecondarySite();
  const routePrefix = secondarySite ? `/${secondarySite}` : '';
  const searchPath = `${routePrefix}/search`;
  const sessionActions = useSessionActions();

  const updateQuery = (query) => {
    setQuery(query);
  };

  const handleSearchTermSelect = useCallback(
    ({ searchTerm, queryType }) => {
      if (!searchTerm) {
        return;
      }
      closeOverlay();
      dispatch(
        trackSearchQuerySubmitted({
          query: searchTerm,
          queryType,
        })
      );
      addHistory(searchTerm);
      sessionActions.updateSessionDetail({
        name: 'search_bar_submission',
        value: '1',
      });
      const searchRoute = {
        pathname: searchPath,
        query: { q: searchTerm },
      };
      Router.push(searchRoute);
      setInputValue(searchTerm);
      updateQuery(searchTerm);
    },
    [closeOverlay, dispatch, addHistory, sessionActions, searchPath]
  );

  const handleProductSelect = useCallback(
    ({ href, product }) => {
      if (!href) {
        return;
      }

      if (product) {
        const productInfo = {
          product: {
            ...product,
            promos: {}, // this is needed due to avoid a runtime error caused by the lack of optional chaning in the shared function getTrackProductDetails
          },
          href,
          fplId: product.featured_product_location_id_list,
          image_url: product.image_url,
        };
        dispatch(
          trackProductClicked({
            ...productInfo,
            productUrl: href,
          })
        );
      }

      // handle user clicking on a product
      closeOverlay();
      Router.push(
        {
          pathname: `${routePrefix}/${productDetailPath}/[identifier]`,
          query: getQueryParametersFromURL(href),
        },
        href
      );
    },
    [closeOverlay, dispatch, routePrefix]
  );

  return (
    <CioAutosuggestContext.Provider
      value={{
        query,
        updateQuery,
        inputValue,
        setInputValue,
        closeOverlay,
        setCloseOverlay,
        handleSearchTermSelect,
        handleProductSelect,
      }}
    >
      {children}
    </CioAutosuggestContext.Provider>
  );
};

CioAutosuggestProvider.propTypes = {
  children: PropTypes.node,
};

export default useCioAutosuggest;
